import { Outlet, useNavigate, useLocation } from 'react-router';
import { useTelegram } from '../../hooks/useTelegram';
import { useEffect, useState, useRef } from 'react';
import { useAppSettings, useUiStore, type UserRole } from '../../stores/appSettings';
import { safeLocalStorage, safeSessionStorage } from '../../utils/storage';
import { getBotInfo, getBotConfig, getUserMemberships, getFeatureFlags, saveUserTimezone, logMiniAppEvent } from '../../utils/api';
import { BottomNav } from './BottomNav';
import { OfflineBanner } from './UI';
import { DeveloperSuperAdminCard } from '../settings/DeveloperSuperAdminCard';

function getSandboxBtnStyle(isActive: boolean): React.CSSProperties {
  return {
    background: isActive 
      ? 'var(--tg-theme-button-color, #3b82f6)' 
      : 'var(--tg-theme-secondary-bg-color, rgba(0, 0, 0, 0.04))',
    border: isActive 
      ? '1px solid var(--tg-theme-button-color, #3b82f6)' 
      : '1px solid var(--tg-theme-hint-color, rgba(0, 0, 0, 0.08))',
    borderRadius: '12px',
    padding: '8px 14px',
    color: isActive 
      ? 'var(--tg-theme-button-text-color, #fff)' 
      : 'var(--tg-theme-text-color, #222)',
    fontSize: '11px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: isActive ? '0 4px 12px rgba(59, 130, 246, 0.2)' : 'none'
  };
}

const normalizeBusinessId = (id: string): string => {
  let cleanId = id.trim();
  if (cleanId.includes('start=biz_')) {
    cleanId = cleanId.split('start=biz_')[1] || '';
  }
  cleanId = cleanId.split('?')[0] || '';
  
  if (cleanId.startsWith('biz_biz_')) {
    cleanId = cleanId.slice(4);
  }
  
  if (!cleanId.startsWith('biz_')) {
    cleanId = `biz_${cleanId}`;
  }
  
  return cleanId;
};

import { getPlatformBridge } from '../../services/platform-bridge';

export const extractStartParam = (tg?: any): string | null => {
  const fromBridge = getPlatformBridge().startParam;
  if (fromBridge) return fromBridge;
  if (tg?.initDataUnsafe?.start_param) {
    return tg.initDataUnsafe.start_param;
  }
  if (typeof window !== 'undefined') {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const fromSearch = searchParams.get('startapp') || searchParams.get('tgWebAppStartParam') || searchParams.get('start');
      if (fromSearch) return fromSearch;

      const rawHash = window.location.hash.slice(1);
      const hashParams = new URLSearchParams(rawHash);
      const fromHash = hashParams.get('tgWebAppStartParam') || hashParams.get('startapp') || hashParams.get('start');
      if (fromHash) return fromHash;

      const tgWebAppData = hashParams.get('tgWebAppData') || searchParams.get('tgWebAppData') || hashParams.get('WebAppData') || searchParams.get('WebAppData');
      if (tgWebAppData) {
        const decoded = decodeURIComponent(tgWebAppData);
        const dataParams = new URLSearchParams(decoded);
        const fromData = dataParams.get('start_param') || dataParams.get('startapp') || dataParams.get('start');
        if (fromData) return fromData;
      }
    } catch {}
  }
  return null;
};

export function Layout(): React.JSX.Element {
  const { initData, haptic, tg } = useTelegram();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [personalDataConsent, setPersonalDataConsent] = useState(false);
  const [activeTab, setActiveTab] = useState<'owner' | 'worker' | 'client' | 'admin'>('owner');
  const [inviteCode, setInviteCode] = useState('');
  const [clientLink, setClientLink] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { 
    currentRole, 
    setCurrentRole, 
    currentBusinessId,
    setCurrentBusinessId, 
    setCurrentWorkerId, 
    config, 
    setConfig,
    isClientMode,
    setIsClientMode,
    realRole,
    setRealRole,
    memberships,
    setMemberships,
    setUserId,
    setSandboxMockUserId,
    switchContext,
    termsAccepted,
    setTermsAccepted,
    setFeatureFlags
  } = useAppSettings();

  const handleSwitchContext = (businessId: string | null, role: UserRole, workerId: string | null) => {
    if (realRole === 'admin') {
      if (role === 'admin') {
        safeLocalStorage.removeItem('admin_sandbox_active');
        setSandboxMockUserId(null);
      } else {
        safeLocalStorage.setItem('admin_sandbox_active', 'true');
        const bizIndex = businessId ? businessId.replace('mock_biz_', '').replace('biz_', '') : '1';
        let mockUser: string | null = null;
        if (role === 'owner') mockUser = `mock_owner_${bizIndex}`;
        else if (role === 'manager') mockUser = `mock_manager_${bizIndex}_1`;
        else if (role === 'assistant') mockUser = `mock_assistant_${bizIndex}_1`;
        else if (role === 'master') mockUser = `mock_master_${bizIndex}_1`;
        else if (role === 'client') mockUser = `mock_client_${bizIndex}_1`;
        setSandboxMockUserId(mockUser);
      }
    }
    if (initData) {
      logMiniAppEvent(
        initData,
        'info',
        `[Context Switch] Role: ${role}, Business: ${businessId || 'none'}`,
        `WorkerId: ${workerId || 'none'}, Sandbox: ${realRole === 'admin' && role !== 'admin'}`
      ).catch(() => {});
    }
    switchContext(businessId, role, workerId);
  };

  // Синхронизация вкладки в выпадающем меню при открытии
  useEffect(() => {
    if (menuOpen) {
      if (currentRole === 'admin') setActiveTab('admin');
      else if (isClientMode || currentRole === 'client') setActiveTab('client');
      else if (currentRole === 'owner') setActiveTab('owner');
      else setActiveTab('worker');
    }
  }, [menuOpen, currentRole, isClientMode]);

  // Управление нативной кнопкой "Назад" в Telegram
  useEffect(() => {
    if (!tg) return;
    const handleBack = () => {
      haptic.impact('light');
      const event = new CustomEvent('app-back-button', { cancelable: true });
      const isPrevented = !window.dispatchEvent(event);
      if (isPrevented) return;
      navigate(-1);
    };

    if (location.pathname !== '/') {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    } else {
      tg.BackButton.hide();
    }

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [location.pathname, tg, navigate, haptic]);

  // Единый глобальный Role & Route Guard — защита от несанкционированного доступа
  useEffect(() => {
    // Блокируем доступ суперадмина к B2B-страницам бизнеса
    if (currentRole === 'admin') {
      const b2bPaths = ['/staff', '/crm', '/bookings', '/reviews', '/onboarding'];
      if (b2bPaths.includes(location.pathname)) {
        haptic.impact('medium');
        navigate('/');
        return;
      }
    }

    const isBypass = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1' ||
      safeLocalStorage.getItem('sandbox_admin_bypass') === 'true' || 
      new URLSearchParams(window.location.search).get('sandbox') === 'true'
    );
    if (isBypass) return; // Разрешаем локальное/стендовое тестирование песочницы

    if (realRole && realRole !== 'admin') {
      // 1. Блокируем доступ к админским страницам для не-админов
      const adminPaths = ['/owners', '/ai-agents', '/logs', '/billing', '/promo', '/finance', '/sandbox'];
      if (adminPaths.includes(location.pathname)) {
        haptic.impact('medium');
        navigate('/');
      }
      // 2. Блокируем несанкционированную смену роли на admin
      if (currentRole === 'admin') {
        setCurrentRole(realRole);
      }
    }
  }, [currentRole, realRole, location.pathname, navigate, haptic, setCurrentRole]);

  // Автоматическая фоновая регистрация клиента и синхронизация списка салонов при смене салона/роли
  useEffect(() => {
    async function syncClientMemberships() {
      if (!initData || !currentBusinessId) return;
      
      // Сначала сделаем GET-запрос к config, чтобы бэкенд автоматически добавил пользователя как клиента в БД
      if (currentRole === 'client') {
        try {
          await getBotConfig(initData);
        } catch (e) {
          console.error('[Layout:sync] Failed to fetch config for B2C registration:', e);
        }
      }
      
      try {
        const membershipsRes = await getUserMemberships(initData);
        if (membershipsRes.ok) {
          setMemberships(membershipsRes.value.memberships);
        }
      } catch (e) {
        console.error('[Layout:sync] Failed to fetch memberships:', e);
      }
    }
    syncClientMemberships();
  }, [initData, currentBusinessId, currentRole, setMemberships]);

  // Обработка Telegram Deep Linking (start_param, startapp, tgWebAppStartParam)
  useEffect(() => {
    const startParam = extractStartParam(tg);
    if (startParam) {
      haptic.impact('medium');
      if (startParam === 'sandbox') {
        safeLocalStorage.setItem('sandbox_admin_bypass', 'true');
        tg?.showAlert?.('Режим тестирования Супер-Администратора активирован! 🛡️');
      } else if (startParam.startsWith('biz_')) {
        let bizId = startParam;
        
        if (startParam.includes('_s-')) {
          const parts = startParam.split('_');
          const rawBizId = parts[0] + '_' + parts[1];
          bizId = normalizeBusinessId(rawBizId);
          
          const servicePart = parts.find(p => p.startsWith('s-'));
          const datePart = parts.find(p => p.startsWith('d-'));
          const timePart = parts.find(p => p.startsWith('t-'));
          
          if (servicePart) safeSessionStorage.setItem('booking_prefill_serviceId', servicePart.slice(2));
          if (datePart) safeSessionStorage.setItem('booking_prefill_date', datePart.slice(2));
          if (timePart) {
            const formattedTime = timePart.slice(2).replace('-', ':');
            safeSessionStorage.setItem('booking_prefill_time', formattedTime);
          }
        } else {
          bizId = normalizeBusinessId(startParam);
        }
        
        setCurrentBusinessId(bizId);
        setIsClientMode(true);
        setCurrentRole('client');
        navigate('/');
      } else if (startParam.startsWith('inv_')) {
        const inviteCode = startParam.slice(4);
        navigate(`/accept-invite/${inviteCode}`);
      }
    }
  }, [tg, navigate, haptic, setCurrentBusinessId, setIsClientMode, setCurrentRole]);

  // Закрытие выпадающего меню при клике снаружи
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Синхронизация роли, членств и конфига с сервером
  useEffect(() => {
    async function fetchData() {
      if (!initData) return;
      const res = await getBotInfo(initData);
      if (res.ok) {
        const role = res.value.profile.role;
        const isAdmin = res.value.profile.isAdmin;
        const isRealAdmin = res.value.profile.isRealAdmin ?? isAdmin;
        const finalRealRole = isRealAdmin ? 'admin' : role;
        setRealRole(finalRealRole);
        setUserId(res.value.profile.userId);
        
        // Автоматическое определение и отправка таймзоны на сервер
        try {
          const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (clientTz) {
            saveUserTimezone(initData, clientTz).catch(() => {});
          }
        } catch (tzErr) {
          console.warn('Failed to detect or save timezone:', tzErr);
        }
        
        // Получаем членства пользователя
        const membershipsRes = await getUserMemberships(initData);
        if (membershipsRes.ok) {
          setMemberships(membershipsRes.value.memberships);
        }
        
        const backendBusinessId = res.value.profile.businessId;

        // Для роли Супер-Админа: если пользователь перешел по ссылке клиента (biz_...) или уже находится в клиентском режиме,
        // сохраняем B2C клиентский режим и не сбрасываем в админ-дашборд!
        const isSandboxActive = safeLocalStorage.getItem('admin_sandbox_active') === 'true';
        const startParam = extractStartParam(tg);
        const hasClientDeepLink = !!startParam?.startsWith('biz_');
        const currentState = useAppSettings.getState();
        const isCurrentlyClient = currentState.isClientMode || currentState.currentRole === 'client';

        if (!isSandboxActive) {
          if (finalRealRole === 'admin') {
            if (hasClientDeepLink || (isCurrentlyClient && currentState.currentBusinessId)) {
              // Сохраняем клиентский режим для суперадмина
              if (!currentState.isClientMode) setIsClientMode(true);
              if (currentState.currentRole !== 'client') setCurrentRole('client');
            } else if (currentState.currentRole !== 'admin' || currentState.currentBusinessId !== null) {
              // В обычном режиме входа без параметров сбрасываем в дашборд платформы
              handleSwitchContext(null, 'admin', null);
            }
          } else {
            // Для обычных пользователей восстанавливаем или ставим дефолт
            if (role === 'client') {
              setIsClientMode(true);
              setCurrentRole('client');
            }
            const activeBizId = useAppSettings.getState().currentBusinessId;
            if (!activeBizId) {
              setCurrentRole(role);
              setCurrentBusinessId(backendBusinessId);
              setCurrentWorkerId(res.value.profile.workerId);
            }
          }
        } else {
          // В песочнице — сохраняем текущий контекст, если он есть
          const activeBizId = useAppSettings.getState().currentBusinessId || backendBusinessId;
          if (!useAppSettings.getState().currentBusinessId) {
            setCurrentBusinessId(backendBusinessId);
            setCurrentWorkerId(res.value.profile.workerId);
          }
          
          // Восстанавливаем sandboxMockUserId, если он сбросился в null, но песочница активна
          const currentMock = useAppSettings.getState().sandboxMockUserId;
          if (!currentMock && finalRealRole === 'admin') {
            const bizId = activeBizId || 'mock_biz_1';
            const bizIndex = bizId.replace('mock_biz_', '').replace('biz_', '');
            const currentRole = useAppSettings.getState().currentRole || 'owner';
            let mockUser: string | null = null;
            if (currentRole === 'owner') mockUser = `mock_owner_${bizIndex}`;
            else if (currentRole === 'manager') mockUser = `mock_manager_${bizIndex}_1`;
            else if (currentRole === 'assistant') mockUser = `mock_assistant_${bizIndex}_1`;
            else if (currentRole === 'master') mockUser = `mock_master_${bizIndex}_1`;
            else if (currentRole === 'client') mockUser = `mock_client_${bizIndex}_1`;
            setSandboxMockUserId(mockUser);
          }
        }
        
        // Получаем также конфиг бизнеса для названия в шапке
        const currentBizId = useAppSettings.getState().currentBusinessId;
        if (currentBizId) {
          const configRes = await getBotConfig(initData);
          if (configRes.ok) {
            setConfig(configRes.value);
          }
        } else {
          setConfig(null);
        }

        // Получаем фиче-флаги
        if (finalRealRole === 'admin') {
          try {
            const flagsRes = await getFeatureFlags(initData);
            if (flagsRes.ok) {
              setFeatureFlags(flagsRes.value.flags);
            }
          } catch {}
        }

        // Логирование успешного старта сессии MiniApp
        const win = typeof window !== 'undefined' ? (window as any) : {};
        const activePlatform = win.Telegram?.WebApp?.platform || win.WebApp?.platform || 'web';
        const activeVersion = win.Telegram?.WebApp?.version || win.WebApp?.version || '1.0';
        logMiniAppEvent(
          initData,
          'info',
          `[Session Start] Role: ${finalRealRole}, Business: ${backendBusinessId || 'none'}`,
          `Platform: ${activePlatform}, SDK Version: ${activeVersion}, User: ${res.value.profile?.userId || 'anonymous'}`
        ).catch(() => {});
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps — location.pathname намеренно исключён, чтобы не перезапускать fetch при навигации
  }, [initData, tg, setCurrentRole, setCurrentBusinessId, setConfig, setIsClientMode, setRealRole, setCurrentWorkerId, setMemberships, setUserId, switchContext, setSandboxMockUserId, setFeatureFlags]);

  // Отдельный эффект: Onboarding redirect при смене маршрута (не перезапускает fetchData)
  useEffect(() => {
    if (!initData) return;
    const { currentBusinessId: bizId, currentRole, isClientMode: clientModeActive } = useAppSettings.getState();
    const startParam = extractStartParam(tg);
    const isInviteRoute = location.pathname.startsWith('/accept-invite');
    if (!bizId && currentRole !== 'admin' && !clientModeActive && !startParam && !isInviteRoute && location.pathname !== '/onboarding') {
      navigate('/onboarding');
    }
  }, [location.pathname, tg, initData, navigate]);

  const roleLabels: Record<string, string> = {
    admin: 'SUPER ADMIN 🛡️',
    owner: 'ВЛАДЕЛЕЦ 🏢',
    manager: 'МЕНЕДЖЕР 👔',
    master: 'МАСТЕР 🛠️',
    assistant: 'ПОМОЩНИК 🤝',
    client: 'КЛИЕНТ 📱'
  };

  const { hideBottomNav } = useUiStore();
  const isSandboxActive = realRole === 'admin' && (currentRole !== 'admin' || isClientMode);
  const isBottomNavVisible = !hideBottomNav;
  const bottomPadding = isBottomNavVisible 
    ? (currentRole === 'admin' && !isClientMode ? 'calc(135px + env(safe-area-inset-bottom))' : 'calc(80px + env(safe-area-inset-bottom))') 
    : '20px';

  if (!termsAccepted) {
    return (
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'var(--tg-viewport-height, 100dvh)',
          background: 'var(--tg-bg)',
          color: 'var(--tg-text)',
          padding: '24px',
          boxSizing: 'border-box',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif'
        }}
        className="animate-in fade-in duration-300"
      >
        <div 
          style={{
            maxWidth: '400px',
            width: '100%',
            background: 'var(--tg-sec-bg)',
            borderRadius: '24px',
            padding: '32px 24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
          className="glass-premium"
        >
          {/* Animated header logo */}
          <div style={{ position: 'relative', margin: '0 auto 8px auto' }}>
            <div 
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '24px',
                background: 'linear-gradient(135deg, var(--tg-theme-button-color, #3b82f6) 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.25)'
              }}
            >
              🤖
            </div>
            <span style={{ position: 'absolute', bottom: '-4px', right: '-4px', fontSize: '18px' }}>✨</span>
          </div>

          <h2 style={{ fontSize: '22px', fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>
            AI TG Business Platform
          </h2>

          <p style={{ fontSize: '13px', color: 'var(--tg-hint)', lineHeight: '1.5', margin: 0 }}>
            Ваша персональная платформа для автоматизации бизнеса, онлайн-записи и ИИ-ассистентов в Telegram.
          </p>

          <div 
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: '16px',
              padding: '16px',
              fontSize: '12px',
              lineHeight: '1.6',
              textAlign: 'left',
              color: 'var(--tg-text)'
            }}
          >
            Продолжая, вы соглашаетесь с условиями использования <strong style={{ color: 'var(--tg-theme-button-color, #3b82f6)' }}>Mini App Telegram</strong>.
            <br />
            <br />
            📖 Пожалуйста, ознакомьтесь с документом оферты: 
            <a 
              href="https://info.biz-klient.ru/oferta" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={() => {
                haptic.impact('light');
              }}
              style={{
                color: 'var(--tg-theme-link-color, #3390ec)',
                fontWeight: 'bold',
                textDecoration: 'underline',
                marginLeft: '4px',
                display: 'inline-block'
              }}
            >
              Публичная оферта о предоставлении услуг
            </a>
          </div>

          <div 
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              fontSize: '12px',
              textAlign: 'left',
              color: 'var(--tg-text)',
              cursor: 'pointer',
              userSelect: 'none',
              padding: '0 4px'
            }}
          >
            <input 
              type="checkbox" 
              id="pd-consent-checkbox"
              checked={personalDataConsent} 
              onChange={(e) => {
                haptic.impact('light');
                setPersonalDataConsent(e.target.checked);
              }} 
              style={{
                marginTop: '3px',
                cursor: 'pointer'
              }}
            />
            <label htmlFor="pd-consent-checkbox" style={{ cursor: 'pointer', lineHeight: '1.4' }}>
              Я даю согласие на обработку моих персональных данных согласно{' '}
              <a 
                href="https://info.biz-klient.ru/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.impact('light');
                }}
                style={{
                  color: 'var(--tg-theme-link-color, #3390ec)',
                  fontWeight: 'bold',
                  textDecoration: 'underline'
                }}
              >
                Политике конфиденциальности
              </a>
            </label>
          </div>

          <button
            onClick={() => {
              if (personalDataConsent) {
                haptic.impact('medium');
                setTermsAccepted(true);
                haptic.success();
              }
            }}
            disabled={!personalDataConsent}
            style={{
              width: '100%',
              background: personalDataConsent 
                ? 'linear-gradient(135deg, var(--tg-theme-button-color, #3b82f6) 0%, #8b5cf6 100%)'
                : 'var(--tg-theme-hint-color, #999999)',
              opacity: personalDataConsent ? 1 : 0.6,
              color: 'var(--tg-theme-button-text-color, #fff)',
              border: 'none',
              borderRadius: '14px',
              padding: '14px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: personalDataConsent ? 'pointer' : 'not-allowed',
              boxShadow: personalDataConsent ? '0 4px 15px rgba(139, 92, 246, 0.2)' : 'none',
              transition: 'all 0.2s ease'
            }}
            className={personalDataConsent ? "active:scale-[0.98]" : ""}
          >
            Принять и продолжить
          </button>
        </div>

        {/* Быстрый доступ для Разработчика / Супер-Админа платформы */}
        <div style={{ maxWidth: '400px', width: '100%', marginTop: '16px' }}>
          <DeveloperSuperAdminCard />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'var(--tg-viewport-height, 100dvh)', paddingBottom: bottomPadding }} className="w-full flex flex-col bg-tg-theme-bg text-tg-theme-text">

      <OfflineBanner />

      {/* 🕹️ Информационная плашка тест-режима для Супер-Админа */}
      {isSandboxActive && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(139, 92, 246, 0.95))',
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#fff',
          fontSize: '11px',
          fontWeight: 'black',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          boxShadow: '0 4px 15px rgba(239, 68, 68, 0.15)',
          zIndex: 9999,
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }} className="animate-in slide-in-from-top duration-300">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ animation: 'pulse 1.5s infinite' }}>🕹️</span>
            <span>Тест: {isClientMode ? 'Клиент (B2C)' : (roleLabels[currentRole] || currentRole)}</span>
          </div>
          <button
            onClick={() => {
              haptic.impact('medium');
              safeLocalStorage.removeItem('admin_sandbox_active');
              setSandboxMockUserId(null);
              setCurrentBusinessId(null);
              setCurrentWorkerId(null);
              setCurrentRole('admin');
              setIsClientMode(false);
              haptic.success();
              navigate('/');
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              padding: '4px 10px',
              borderRadius: '8px',
              fontSize: '9px',
              fontWeight: 'bold',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              transition: 'all 0.2s ease'
            }}
            className="hover:bg-white/30 active:scale-95"
          >
            Выйти из теста 🛡️
          </button>
        </div>
      )}

      <header className="header-status animate-in fade-in duration-300" style={{ 
        position: 'relative',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '2px 16px 6px 16px', 
        background: 'var(--tg-theme-section-bg-color, rgba(255,255,255,0.03))', 
        borderBottom: '1px solid var(--tg-theme-hint-color, rgba(255,255,255,0.06))',
        zIndex: 1000,
        gap: '12px'
      }}>
        {/* Dynamic Context Switcher Trigger */}
        <div 
          ref={menuRef}
          onClick={() => {
            haptic.impact('light');
            setMenuOpen(!menuOpen);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '14px',
            background: menuOpen ? 'var(--tg-theme-secondary-bg-color, rgba(0, 0, 0, 0.05))' : 'transparent',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid rgba(255, 255, 255, 0.03)',
            flex: 1,
            minWidth: 0
          }}
          className="hover:bg-black/5 dark:hover:bg-white/5 active:scale-98"
        >
          <div className="status-item" style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
            <span className="status-label">
              {isClientMode ? 'Клиентский вид' : (currentRole === 'owner' ? 'Бизнес' : 'Роль')}
            </span>
            <span className="status-value" style={{ 
              fontWeight: 'bold', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              color: isClientMode ? '#8b5cf6' : 'var(--tg-theme-button-color, #3b82f6)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {isClientMode 
                ? 'Запись онлайн 📱' 
                : (currentRole === 'owner' 
                    ? (config?.businessName || 'Мой бизнес 🏢') 
                    : (roleLabels[currentRole] || 'ГОСТЬ'))}
              
              <svg 
                style={{ 
                  transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: '12px',
                  height: '12px',
                  fill: 'currentColor',
                  opacity: 0.7,
                  flexShrink: 0
                }} 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          </div>
        </div>

        {/* Action Button: Platform / B2B panel quick status action */}
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {realRole === 'admin' && (currentRole !== 'admin' || isClientMode) && (
            <button
              onClick={() => {
                haptic.impact('medium');
                handleSwitchContext(null, 'admin', null);
                haptic.success();
                navigate('/');
              }}
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.22))',
                border: '1px solid rgba(239, 68, 68, 0.35)',
                borderRadius: '12px',
                padding: '8px 12px',
                color: '#ef4444',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.08)'
              }}
              className="hover:scale-105 active:scale-95 font-sans"
            >
              🛡️ Платформа
            </button>
          )}

          {currentRole !== 'admin' && (
            <button
              onClick={() => {
                haptic.impact('medium');
                setIsClientMode(!isClientMode);
                navigate('/');
              }}
              style={{
                background: 'var(--tg-theme-secondary-bg-color, rgba(0, 0, 0, 0.05))',
                border: '1px solid var(--tg-theme-hint-color, rgba(0, 0, 0, 0.1))',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'var(--tg-theme-text-color)',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
              }}
              className="hover:scale-105 active:scale-95"
            >
              {isClientMode ? '💼 Панель B2B' : '📱 Клиент'}
            </button>
          )}
        </div>

        {/* Floating Dropdown Context Selector */}
        {menuOpen && (
          <div 
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: '16px',
              right: '16px',
              background: 'rgba(var(--tg-theme-bg-color-rgb, 255, 255, 255), 0.75)',
              backdropFilter: 'blur(25px)',
              WebkitBackdropFilter: 'blur(25px)',
              border: '1px solid var(--tg-theme-hint-color, rgba(255, 255, 255, 0.08))',
              borderRadius: '20px',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
              padding: '14px',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            className="dropdown-menu-container"
          >
            <div style={{ 
              fontSize: '10px', 
              fontWeight: 800, 
              color: 'var(--tg-theme-hint-color, rgba(0,0,0,0.4))', 
              textTransform: 'uppercase', 
              letterSpacing: '0.08em', 
              padding: '4px 8px 8px 8px',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Выбор роли и филиала</span>
              <span style={{ fontSize: '8px', background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: '6px' }}>
                {memberships.length} членств
              </span>
            </div>

            {/* Вкладки ролей */}
            <div style={{ display: 'flex', gap: '4px', background: 'var(--tg-theme-secondary-bg-color, rgba(0,0,0,0.04))', padding: '3px', borderRadius: '14px', marginBottom: '4px' }}>
              {realRole === 'admin' && !isSandboxActive && (
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    haptic.impact('medium'); 
                    setActiveTab('admin'); 
                    handleSwitchContext(null, 'admin', null);
                    haptic.success();
                    navigate('/');
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 2px',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    background: activeTab === 'admin' ? 'var(--tg-theme-bg-color, #fff)' : 'transparent',
                    border: 'none',
                    color: activeTab === 'admin' ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                    boxShadow: activeTab === 'admin' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Админ 🛡️
                </button>
              )}
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  haptic.impact('medium'); 
                  setActiveTab('owner'); 
                  const ownerMemberships = memberships.filter(m => m.role === 'owner');
                  if (ownerMemberships.length > 0) {
                    const currentIsOwner = currentRole === 'owner' && ownerMemberships.some(m => m.businessId === currentBusinessId);
                    const target = currentIsOwner ? ownerMemberships.find(m => m.businessId === currentBusinessId) : ownerMemberships[0];
                    if (target) {
                      switchContext(target.businessId, 'owner', null);
                    }
                    navigate('/');
                  } else {
                    switchContext(null, 'owner', null);
                    setMenuOpen(false);
                    navigate('/onboarding');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 2px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  background: activeTab === 'owner' ? 'var(--tg-theme-bg-color, #fff)' : 'transparent',
                  border: 'none',
                  color: activeTab === 'owner' ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                  boxShadow: activeTab === 'owner' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Владелец 🏢
              </button>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  haptic.impact('medium'); 
                  setActiveTab('worker'); 
                  const workerMemberships = memberships.filter(m => ['master', 'assistant', 'manager'].includes(m.role));
                  if (workerMemberships.length > 0) {
                    const currentIsWorker = ['master', 'assistant', 'manager'].includes(currentRole) && workerMemberships.some(m => m.businessId === currentBusinessId && m.role === currentRole);
                    const target = currentIsWorker ? workerMemberships.find(m => m.businessId === currentBusinessId && m.role === currentRole) : workerMemberships[0];
                    if (target) {
                      switchContext(target.businessId, target.role, target.workerId);
                    }
                    navigate('/');
                  } else {
                    switchContext(null, 'master', null);
                    navigate('/');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 2px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  background: activeTab === 'worker' ? 'var(--tg-theme-bg-color, #fff)' : 'transparent',
                  border: 'none',
                  color: activeTab === 'worker' ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                  boxShadow: activeTab === 'worker' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Сотрудник 💼
              </button>
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  haptic.impact('medium'); 
                  setActiveTab('client'); 
                  const clientMemberships = memberships.filter(m => m.role === 'client');
                  if (clientMemberships.length > 0) {
                    const currentIsClient = currentRole === 'client' && clientMemberships.some(m => m.businessId === currentBusinessId);
                    const target = currentIsClient ? clientMemberships.find(m => m.businessId === currentBusinessId) : clientMemberships[0];
                    if (target) {
                      switchContext(target.businessId, 'client', null);
                    }
                    navigate('/');
                  } else {
                    switchContext(null, 'client', null);
                    navigate('/');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px 2px',
                  borderRadius: '10px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  background: activeTab === 'client' ? 'var(--tg-theme-bg-color, #fff)' : 'transparent',
                  border: 'none',
                  color: activeTab === 'client' ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                  boxShadow: activeTab === 'client' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Клиент 📱
              </button>
            </div>

            {/* Содержимое вкладок */}
            <div onClick={(e) => e.stopPropagation()} style={{ minHeight: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              
              {/* Вкладка 1: Суперадмин */}
              {activeTab === 'admin' && realRole === 'admin' && (
                <button
                  onClick={() => {
                    haptic.impact('medium');
                    handleSwitchContext(null, 'admin', null);
                    setMenuOpen(false);
                    haptic.success();
                    navigate('/');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '14px',
                    background: (currentRole === 'admin' && !isClientMode) 
                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(139, 92, 246, 0.1))' 
                      : 'transparent',
                    border: (currentRole === 'admin' && !isClientMode)
                      ? '1px solid rgba(239, 68, 68, 0.2)'
                      : '1px solid transparent',
                    color: 'var(--tg-theme-text-color)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>🛡️</span>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold' }}>Панель Платформы</div>
                      <div style={{ fontSize: '10px', color: 'var(--tg-theme-hint-color)' }}>Супер-Администратор</div>
                    </div>
                  </div>
                  {(currentRole === 'admin' && !isClientMode) && (
                    <span style={{ color: '#ef4444', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                  )}
                </button>
              )}

              {/* Вкладка 2: Владелец бизнеса */}
              {activeTab === 'owner' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {memberships.filter(m => m.role === 'owner').length === 0 ? (
                    <div style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--tg-theme-hint-color)', fontSize: '12px', fontWeight: 600 }}>
                      У вас нет зарегистрированных филиалов.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '180px', overflowY: 'auto' }} className="no-scrollbar">
                      {memberships.filter(m => m.role === 'owner').map(m => {
                        const isActive = currentBusinessId === m.businessId && currentRole === 'owner' && !isClientMode;
                        return (
                          <button
                            key={m.businessId}
                            onClick={() => {
                              haptic.impact('medium');
                              handleSwitchContext(m.businessId, 'owner', null);
                              setMenuOpen(false);
                              haptic.success();
                              navigate('/');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '12px',
                              background: isActive ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                              border: isActive ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid transparent',
                              color: 'var(--tg-theme-text-color)',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            className="hover:bg-white/5"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>🏢</span>
                              <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{m.businessName}</div>
                            </div>
                            {isActive && <span style={{ color: 'var(--tg-theme-button-color)', fontWeight: 'bold' }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      haptic.impact('medium');
                      setMenuOpen(false);
                      navigate('/onboarding');
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '12px',
                      background: 'var(--tg-theme-button-color, #3b82f6)',
                      color: 'var(--tg-theme-button-text-color, #fff)',
                      border: 'none',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginTop: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    className="active:scale-[0.98]"
                  >
                    ＋ Создать новый салон
                  </button>
                </div>
              )}

              {/* Вкладка 3: Сотрудник */}
              {activeTab === 'worker' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {memberships.filter(m => ['master', 'assistant', 'manager'].includes(m.role)).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflowY: 'auto' }} className="no-scrollbar">
                      {memberships.filter(m => ['master', 'assistant', 'manager'].includes(m.role)).map(m => {
                        const isActive = currentBusinessId === m.businessId && currentRole === m.role && !isClientMode;
                        const roleLabels: Record<string, string> = { manager: 'Менеджер 👔', master: 'Мастер 🛠️', assistant: 'Помощник 🤝' };
                        return (
                          <button
                            key={`${m.businessId}-${m.role}`}
                            onClick={() => {
                              haptic.impact('medium');
                              handleSwitchContext(m.businessId, m.role, m.workerId);
                              setMenuOpen(false);
                              haptic.success();
                              navigate('/');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '12px',
                              background: isActive ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                              border: isActive ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid transparent',
                              color: 'var(--tg-theme-text-color)',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            className="hover:bg-white/5"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>💼</span>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{m.businessName}</div>
                                <div style={{ fontSize: '9px', color: 'var(--tg-theme-hint-color)', textTransform: 'uppercase', fontWeight: 'bold' }}>
                                  {roleLabels[m.role] || m.role}
                                </div>
                              </div>
                            </div>
                            {isActive && <span style={{ color: 'var(--tg-theme-button-color)', fontWeight: 'bold' }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Ввод инвайт-кода */}
                  <div style={{ 
                    background: 'var(--tg-theme-secondary-bg-color, rgba(0,0,0,0.03))', 
                    border: '1px solid rgba(255,255,255,0.04)', 
                    padding: '10px', 
                    borderRadius: '14px', 
                    marginTop: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--tg-theme-hint-color)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Присоединиться к команде:
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input 
                        type="text" 
                        placeholder="Вставьте код или ссылку..."
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        style={{
                          flex: 1,
                          background: 'var(--tg-theme-bg-color, #fff)',
                          border: '1px solid var(--tg-theme-hint-color, rgba(0,0,0,0.15))',
                          borderRadius: '10px',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: 'var(--tg-theme-text-color)',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={() => {
                          let codeStr = inviteCode.trim();
                          if (!codeStr) return;
                          haptic.impact('medium');
                          if (codeStr.includes('start=inv_')) {
                            codeStr = codeStr.split('start=inv_')[1] || '';
                          } else if (codeStr.includes('accept-invite/')) {
                            codeStr = codeStr.split('accept-invite/')[1] || '';
                          }
                          codeStr = codeStr.split('?')[0] || '';
                          
                          setInviteCode('');
                          setMenuOpen(false);
                          navigate(`/accept-invite/${codeStr}`);
                        }}
                        style={{
                          background: 'var(--tg-theme-button-color, #3b82f6)',
                          color: 'var(--tg-theme-button-text-color, #fff)',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '8px 12px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        className="active:scale-[0.95]"
                      >
                        Войти
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Вкладка 4: Клиент */}
              {activeTab === 'client' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {memberships.filter(m => m.role === 'client').length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflowY: 'auto' }} className="no-scrollbar">
                      {memberships.filter(m => m.role === 'client').map(m => {
                        const isActive = currentBusinessId === m.businessId && isClientMode;
                        return (
                          <button
                            key={`${m.businessId}-client`}
                            onClick={() => {
                              haptic.impact('medium');
                              handleSwitchContext(m.businessId, 'client', null);
                              setMenuOpen(false);
                              haptic.success();
                              navigate('/');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              padding: '10px 12px',
                              borderRadius: '12px',
                              background: isActive ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                              border: isActive ? '1px solid rgba(139, 92, 246, 0.2)' : '1px solid transparent',
                              color: 'var(--tg-theme-text-color)',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                            className="hover:bg-white/5"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>📱</span>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{m.businessName}</div>
                                <div style={{ fontSize: '9px', color: 'var(--tg-theme-hint-color)' }}>
                                  Онлайн-запись B2C
                                </div>
                              </div>
                            </div>
                            {isActive && <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Ввод B2C ссылки */}
                  <div style={{ 
                    background: 'var(--tg-theme-secondary-bg-color, rgba(0,0,0,0.03))', 
                    border: '1px solid rgba(255,255,255,0.04)', 
                    padding: '10px', 
                    borderRadius: '14px', 
                    marginTop: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: 'var(--tg-theme-hint-color)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Записаться в новый салон:
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input 
                        type="text" 
                        placeholder="ID бизнеса или ссылка..."
                        value={clientLink}
                        onChange={(e) => setClientLink(e.target.value)}
                        style={{
                          flex: 1,
                          background: 'var(--tg-theme-bg-color, #fff)',
                          border: '1px solid var(--tg-theme-hint-color, rgba(0,0,0,0.15))',
                          borderRadius: '10px',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: 'var(--tg-theme-text-color)',
                          outline: 'none'
                        }}
                      />
                      <button
                        onClick={() => {
                          const linkStr = normalizeBusinessId(clientLink);
                          if (!linkStr) return;
                          haptic.impact('medium');
                          
                          setClientLink('');
                          setMenuOpen(false);
                          setCurrentBusinessId(linkStr);
                          setIsClientMode(true);
                          setCurrentRole('client');
                          haptic.success();
                          navigate('/');
                        }}
                        style={{
                          background: '#8b5cf6',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '8px 12px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        className="active:scale-[0.95]"
                      >
                        Запись
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Карточка разработчика и SuperAdmin активации */}
            <div style={{ marginTop: '8px' }}>
              <DeveloperSuperAdminCard />
            </div>
          </div>
        )}
      </header>

      <main className="container" style={{ flex: 1, width: '100%', padding: '8px 16px 85px 16px' }}>
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

